import type {
    IdibuPortal,
    IdibuOrgConfig,
    PortalCategory,
    PortalCountry,
} from '../services/idibu.api';

/**
 * Realistic, conservative fixture data for the Idibu integration demo mode.
 * No fabricated metrics, no marketing copy. Just enough to make the page
 * render its connected/marketplace state with recognisable boards.
 *
 * `logo` holds a full https URL (Clearbit by domain). The components were
 * extended to detect full URLs and render them directly; otherwise the
 * existing idibu CDN behavior is preserved.
 */

// Subscribed portal IDs — Indeed + LinkedIn pre-connected.
export const DEMO_SUBSCRIBED_IDS = new Set<number>([1001, 1002]);

interface DemoPortal extends IdibuPortal {
    popularRank: number;
}

// `logo` is stored as a bare domain — the logo helper expands that to a
// fallback chain (Clearbit → DuckDuckGo → Google), so a single 404 from
// any one provider doesn't leave us with a broken image.
export const DEMO_PORTALS: DemoPortal[] = [
    {
        id: 1001,
        name: 'Indeed',
        url: 'https://www.indeed.com',
        logo: 'indeed.com',
        tags: 'General;Global',
        country: 'Global',
        description: 'Worldwide aggregator with the largest job seeker traffic.',
        popularRank: 1,
    },
    {
        id: 1002,
        name: 'LinkedIn',
        url: 'https://www.linkedin.com',
        logo: 'linkedin.com',
        tags: 'General;Executive;Tech',
        country: 'Global',
        description: 'Professional network for white-collar and executive hiring.',
        popularRank: 2,
    },
    {
        id: 1003,
        name: 'Glassdoor',
        url: 'https://www.glassdoor.com',
        logo: 'glassdoor.com',
        tags: 'General',
        country: 'US',
        description: 'Job search alongside company reviews and salary data.',
        popularRank: 3,
    },
    {
        id: 1004,
        name: 'ZipRecruiter',
        url: 'https://www.ziprecruiter.com',
        logo: 'ziprecruiter.com',
        tags: 'General',
        country: 'US',
        description: 'Distributes one posting to 100+ partner boards.',
        popularRank: 4,
    },
    {
        id: 1005,
        name: 'Monster',
        url: 'https://www.monster.com',
        logo: 'monster.com',
        tags: 'General',
        country: 'Global',
        description: 'Long-running general jobs board with global reach.',
        popularRank: 5,
    },
    {
        id: 1006,
        name: 'CareerBuilder',
        url: 'https://www.careerbuilder.com',
        logo: 'careerbuilder.com',
        tags: 'General',
        country: 'US',
        description: 'US general board with strong mid-market employer base.',
        popularRank: 6,
    },
    {
        id: 1007,
        name: 'SimplyHired',
        url: 'https://www.simplyhired.com',
        logo: 'simplyhired.com',
        tags: 'General',
        country: 'US',
        description: 'Aggregator covering hourly and salaried roles in the US.',
        popularRank: 7,
    },
    {
        id: 1008,
        name: 'Dice',
        url: 'https://www.dice.com',
        logo: 'dice.com',
        tags: 'Tech',
        country: 'US',
        description: 'Specialist tech and engineering jobs board.',
        popularRank: 8,
    },
    {
        id: 1009,
        name: 'Reed',
        url: 'https://www.reed.co.uk',
        logo: 'reed.co.uk',
        tags: 'General',
        country: 'GB',
        description: 'One of the largest UK general job boards.',
        popularRank: 9,
    },
    {
        id: 1010,
        name: 'Totaljobs',
        url: 'https://www.totaljobs.com',
        logo: 'totaljobs.com',
        tags: 'General',
        country: 'GB',
        description: 'UK general board, part of the StepStone group.',
        popularRank: 10,
    },
    {
        id: 1011,
        name: 'CV-Library',
        url: 'https://www.cv-library.co.uk',
        logo: 'cv-library.co.uk',
        tags: 'General',
        country: 'GB',
        description: 'UK CV database and job posting service.',
        popularRank: 11,
    },
    {
        id: 1012,
        name: 'Adzuna',
        url: 'https://www.adzuna.com',
        logo: 'adzuna.com',
        tags: 'General',
        country: 'Global',
        description: 'Search engine for jobs across 20+ countries.',
        popularRank: 12,
    },
    {
        id: 1013,
        name: 'Seek',
        url: 'https://www.seek.com.au',
        logo: 'seek.com.au',
        tags: 'General',
        country: 'AU',
        description: 'Dominant general jobs board in Australia and New Zealand.',
        popularRank: 13,
    },
    {
        id: 1014,
        name: 'Jora',
        url: 'https://au.jora.com',
        logo: 'jora.com',
        tags: 'General',
        country: 'AU',
        description: 'Aggregator covering AU, NZ, and select international markets.',
        popularRank: 14,
    },
    {
        id: 1015,
        name: 'Naukri',
        url: 'https://www.naukri.com',
        logo: 'naukri.com',
        tags: 'General;Tech',
        country: 'IN',
        description: 'Largest job portal in India.',
        popularRank: 15,
    },
    {
        id: 1016,
        name: 'JobStreet',
        url: 'https://www.jobstreet.com',
        logo: 'jobstreet.com',
        tags: 'General',
        country: 'MY',
        description: 'Leading job board across South-East Asia.',
        popularRank: 16,
    },
    {
        id: 1017,
        name: 'StepStone',
        url: 'https://www.stepstone.com',
        logo: 'stepstone.com',
        tags: 'General;Executive',
        country: 'DE',
        description: 'Premier general jobs board in Germany and continental EU.',
        popularRank: 17,
    },
    {
        id: 1018,
        name: 'Welcome to the Jungle',
        url: 'https://www.welcometothejungle.com',
        logo: 'welcometothejungle.com',
        tags: 'General;Creative;Tech',
        country: 'FR',
        description: 'Employer-branded job board popular with European startups.',
        popularRank: 18,
    },
];

export const DEMO_CATEGORIES: PortalCategory[] = [
    { category: 'General' },
    { category: 'Tech' },
    { category: 'Executive' },
    { category: 'Healthcare' },
    { category: 'Finance' },
    { category: 'Creative' },
    { category: 'Hospitality' },
    { category: 'Trades' },
];

// Subset of the 35-country list in useIdibuCountries — enough for the
// filter dropdown to feel realistic without listing everything.
export const DEMO_COUNTRIES: PortalCountry[] = [
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'AU', name: 'Australia' },
    { code: 'CA', name: 'Canada' },
    { code: 'IE', name: 'Ireland' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'ES', name: 'Spain' },
    { code: 'IT', name: 'Italy' },
    { code: 'IN', name: 'India' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'SG', name: 'Singapore' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'Global', name: 'Global / Multi-country' },
];

// Connected ~3 weeks before today; shifts forward as time passes so
// the date in the status card never reads as suspiciously stale.
function recentISODate(daysAgo: number): string {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    d.setHours(9, 14, 0, 0);
    return d.toISOString();
}

export const DEMO_CONFIG: IdibuOrgConfig = {
    accountHash: '****ABCD',
    clientId: 47213,
    isActive: true,
    provisionedByPartner: false,
    connectedAt: recentISODate(21),
    hasStoredHash: true,
};
